import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { getApiErrorMessage } from 'src/api/axiosClient/errorParser';
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { GENERIC_ERROR_MESSAGE } from 'src/constants/messages';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useGroupService } from 'src/modules/groups/services';
import { joinCampaignsToOffer } from '../helpers/joinCampaignsToOffer';
import { useCampaignService } from '../services';
import { dismissJoinCampaignOffer, getJoinCampaignOffer, queueJoinCampaignOffer } from '../slices';
import { JoinCampaignOffer, JoinCampaignOption } from '../types';

export const JOIN_CAMPAIGNS_LOAD_ERROR = "Couldn't load the join campaigns of the groups.";
export const SOME_JOIN_CAMPAIGNS_LOAD_ERROR = "Couldn't load the join campaigns of some of the groups.";

/**
 * Returns a function that loads the active join campaigns of the groups, each once.
 *
 * `includeDefaultGroup` adds the default group's join campaigns, since every contact is in it, even when no
 * other group was chosen.
 */
export const useJoinCampaignsLoader = () => {
  const companyId = useSelector(getCurrentCompanyId);
  const { getJoinCampaigns, getDefaultGroupJoinCampaigns } = useGroupService();

  return useCallback(
    async (groupIds: string[], includeDefaultGroup = false): Promise<JoinCampaignOption[]> => {
      if (!companyId) return [];
      const requests = [
        ...(groupIds.length > 0 ? [() => getJoinCampaigns(companyId, [...new Set(groupIds)])] : []),
        ...(includeDefaultGroup ? [() => getDefaultGroupJoinCampaigns(companyId)] : []),
      ];

      const results = await Promise.allSettled(requests.map(load => load()));
      const failures = results.flatMap(r => (r.status === 'rejected' ? [r.reason] : []));
      if (failures.length > 0) {
        reportErrorToUser(
          failures[0],
          failures.length === results.length ? JOIN_CAMPAIGNS_LOAD_ERROR : SOME_JOIN_CAMPAIGNS_LOAD_ERROR,
        );
      }

      return joinCampaignsToOffer(results.flatMap(r => (r.status === 'fulfilled' ? [r.value] : [])));
    },
    [companyId, getJoinCampaigns, getDefaultGroupJoinCampaigns],
  );
};

/**
 * Returns a function to call once contacts are added to groups, when the user chose to start one of the
 * campaigns: it opens the start join campaign dialog with them.
 */
export const useJoinCampaignOffer = () => {
  const dispatch = useDispatch();
  const companyId = useSelector(getCurrentCompanyId);

  return useCallback(
    (contactIds: string[], campaigns: JoinCampaignOption[]) => {
      if (!companyId || contactIds.length === 0 || campaigns.length === 0) return;
      dispatch(queueJoinCampaignOffer({ companyId, contactIds: [...new Set(contactIds)], campaigns }));
    },
    [companyId, dispatch],
  );
};

export interface StartJoinCampaignDialogController {
  offer: JoinCampaignOffer | null;
  selectedCampaignId: string | null;
  loading: boolean;
  select: (campaignId: string) => void;
  start: () => Promise<void>;
  close: () => void;
}

export const useStartJoinCampaignDialogController = (): StartJoinCampaignDialogController => {
  const dispatch = useDispatch();
  const offer = useSelector(getJoinCampaignOffer);
  const { loading, startCampaign } = useCampaignService();
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);

  useEffect(() => {
    // A single campaign is preselected, several are left for the user to choose from.
    setSelectedCampaignId(offer?.campaigns.length === 1 ? offer.campaigns[0].id : null);
  }, [offer]);

  // Shows the next queued offer, if any.
  const close = useCallback(() => dispatch(dismissJoinCampaignOffer()), [dispatch]);

  const start = useCallback(async () => {
    const campaign = offer?.campaigns.find(c => c.id === selectedCampaignId);
    if (!offer || !campaign || loading.start) return;
    try {
      await startCampaign(offer.companyId, campaign.id, offer.contactIds);
      const count = offer.contactIds.length;
      toast.success(`${campaign.name} started for ${count} ${count === 1 ? 'contact' : 'contacts'}`);
      close();
    } catch (e) {
      reportErrorToUser(e, getApiErrorMessage(e, GENERIC_ERROR_MESSAGE));
    }
  }, [offer, selectedCampaignId, loading.start, startCampaign, close]);

  return {
    offer,
    selectedCampaignId,
    loading: loading.start,
    select: setSelectedCampaignId,
    start,
    close,
  };
};
